import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { formatDate } from '@/util/dates';
import type {
    ApplyDocumentTrimRequest,
    BrandId,
    DocumentDates,
    ModelDocument,
    ModelDocumentCandidate,
    ModelDocumentId,
    PageRange,
    TrimCaution,
    TrimTarget,
} from '@rock/types';
import {
    APP_VISIBLE_TAGS,
    DOC_TAG_LABELS,
    type DocumentCoverage,
    type DocumentLanguageAnalysis,
    type DocumentTrimProposal,
    METADATA_TAGS,
    type ModelDocTag,
    ModelDocTagSchema,
} from '@rock/types';
import {
    type DocumentLinkFit,
    documentTitle,
    formatCoverageRange,
    formatPageRanges,
    isAppVisibleDocument,
    isUploadedSource,
    parsePageRanges,
    collapseToRanges,
    storagePathFromUrl,
    summarizeLanguagePages,
    TRIM_REVISION,
    uncertainTrimPages,
} from '@rock/manuals';
import { ChevronDown, ExternalLink, RefreshCw, X } from 'lucide-react';
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BrandChip } from '@/components/BrandChip';
import { HintTooltip } from '@/components/HintTooltip';
import { allBrandsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { COVERAGE_HINT, CoverageFields, type CoveragePublishTarget, publishLinksTarget } from './CoverageFields';
import type { CoverageDraft, CoverageSuggestion } from './coverageDraft';
import { FormRow } from './FormRow';
import { DocumentScopeFields, type DocumentScopeFieldsProps } from './DocumentScopeFields';
import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { brandsById } from './modelBrand';

// Shared presentation pieces for documents: the model page's cards (published docs, acquisition,
// candidates) and the document pages.

/**
 * The document's own edition date, as a short "May 2015" — the ONLY date on a candidate that describes
 * the document rather than our handling of it (`triedAt` is when we fetched it). Month granularity
 * because that is the question review actually asks: is this the current generation of the product, or a
 * volume from two refreshes ago?
 *
 * Returns undefined when the PDF reported no usable date, which is common — the caller renders nothing
 * rather than a placeholder, so an absent date never reads as a claim about the document.
 */
export function describeDocumentDates(dates: DocumentDates | undefined): { label: string; title: string } | undefined {
    const stamp = dates?.createdAt ?? dates?.modifiedAt;
    const month = stamp ? editionMonth(stamp) : undefined;
    if (!month) return undefined;
    const lines = [
        dates?.createdAt ? `Created ${formatDate(dates.createdAt.slice(0, 10))}` : undefined,
        // Worth spelling out: a re-export bumps ModDate without the edition changing, so a recent
        // modified date next to an old created date is not evidence of a newer volume.
        dates?.modifiedAt ? `File modified ${formatDate(dates.modifiedAt.slice(0, 10))}` : undefined,
        "From the PDF's own metadata — not when we found it.",
    ].filter(Boolean);
    // Falling back to ModDate is a WEAKER claim, so the chip says which one it is rather than letting a
    // re-export date read as the edition. The tooltip's caveat only helps a reviewer who hovers.
    return { label: `${dates?.createdAt ? 'dated' : 'file dated'} ${month}`, title: lines.join('\n') };
}

/**
 * "May 2015" from a timestamp's OWN calendar parts, never from the instant it denotes.
 *
 * A document's stated date is a wall-clock claim by its author, not a moment to re-project: rendering
 * `2015-05-01T00:00:00Z` through the viewer's zone shows an admin in Los Angeles "Apr 2015". The chip's
 * entire payload is the month, so that flips the one fact it exists to convey — in a feature whose whole
 * premise (`isoTimestamp` in the worker's pdfDocInfo) is that a confidently wrong date beats no date only
 * in the wrong direction. Building a local `Date` from the parts keeps `Intl`'s localized month name
 * without letting it shift the calendar. Same trap `toDate` in `@/util/dates` exists for, one field over.
 */
function editionMonth(stamp: string): string | undefined {
    if (!/^\d{4}-\d{2}-\d{2}/.test(stamp)) return undefined;
    const local = new Date(Number(stamp.slice(0, 4)), Number(stamp.slice(5, 7)) - 1, Number(stamp.slice(8, 10)));
    return local.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

/**
 * Human label for a document URL. An offered FILE is a real, linkable URL like any other, but it points
 * at our own Storage — so `firebasestorage.googleapis.com/…/{hash}.pdf` would name the plumbing instead
 * of the act. Every surface that shows a URL (candidate URL lists, a document's Source, search history)
 * says "Uploaded file" for those, which is why the test is the `offers/` PREFIX and not the hostname.
 */
export function describeUrl(url: string | undefined): { host: string; label: string } | undefined {
    if (!url) return undefined;
    try {
        const parsed = new URL(url);
        // Our own host only means "uploaded" under the `offers/` prefix — that's where an admin's file
        // is parked, and it's the only address those bytes ever had. Any other prefix on the same host
        // is a self-reference, not provenance.
        if (isUploadedSource(url)) return { host: parsed.hostname, label: 'Uploaded file' };
        // Another of our own prefixes (a `trimmed/` copy, a staged candidate): still not an external
        // source, so naming the bucket path would be the same plumbing-instead-of-the-act problem.
        if (storagePathFromUrl(url)) return { host: parsed.hostname, label: 'Our stored copy' };
        const segments = parsed.pathname.split('/').filter(Boolean);
        const tail = segments.length ? segments[segments.length - 1] : '';
        return { host: parsed.hostname, label: tail ? `${parsed.hostname}/…/${tail}` : parsed.hostname };
    } catch {
        return { host: url, label: url };
    }
}

/** Small tag chips, in tag order. */
export const TagChips: FC<{ tags: ModelDocTag[] }> = ({ tags }) => (
    <>
        {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="border-transparent bg-slate-100 text-slate-900">
                {DOC_TAG_LABELS[tag]}
            </Badge>
        ))}
    </>
);

/** What a document is for, as pills: its brands and its equipment types. Sits with the tags, the same kind of fact. */
export const DocumentScopePills: FC<{ doc: Pick<ModelDocument, 'brandIds' | 'typeIds'> }> = ({ doc }) => (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">For:</span>
        <DocumentBrandChips brandIds={doc.brandIds} />
        {doc.typeIds.map((typeId) => (
            <EquipmentTypeBadge key={typeId} typeId={typeId} />
        ))}
        {doc.brandIds.length + doc.typeIds.length === 0 && <MissingDocumentScope />}
    </div>
);

/** A document's brands as chips; an id the catalog no longer has shows raw rather than vanishing. */
export const DocumentBrandChips: FC<{ brandIds: BrandId[] }> = ({ brandIds }) => {
    const brands = useAsyncAtomValue(allBrandsState, []);
    const byId = useMemo(() => brandsById(brands), [brands]);
    return (
        <>
            {brandIds.map((brandId) => {
                const brand = byId.get(brandId);
                return brand ? (
                    <BrandChip key={brandId} brand={brand} className="h-6 text-xs" />
                ) : (
                    <span key={brandId} className="font-mono text-xs">
                        {brandId}
                    </span>
                );
            })}
        </>
    );
};

/** Shown for a document stored with no brand or type. */
export const MissingDocumentScope: FC = () => (
    <span className="text-xs text-amber-700">No brand or type yet: edit the publication to add them.</span>
);

/** A link to a document's page, by title once the document has loaded (by id until then). */
export const DocumentLink: FC<{ id: ModelDocumentId; doc: ModelDocument | undefined }> = ({ id, doc }) => (
    <Link
        to={`/products/documents/${encodeURIComponent(id)}`}
        className="font-medium underline-offset-4 hover:underline"
    >
        {doc ? documentTitle(doc) : id}
    </Link>
);

/** The document's own state, as badges: library-only, digest outcome, page cut. */
export const DocumentStateBadges: FC<{ doc: ModelDocument }> = ({ doc }) => (
    <>
        {!isAppVisibleDocument(doc) && (
            <Badge variant="secondary" className="border-transparent bg-slate-200 text-slate-700">
                Not in app
            </Badge>
        )}
        {doc.ingest?.status === 'ingested' && (
            <Badge variant="secondary" className="border-transparent bg-emerald-100 text-emerald-900">
                Digested
            </Badge>
        )}
        {(doc.ingest?.status === 'failed' || doc.ingest?.status === 'unsupported') && (
            <Badge variant="destructive">Ingest {doc.ingest.status}</Badge>
        )}
        {doc.trim && (
            <Badge variant="secondary" className="border-transparent bg-purple-100 text-purple-900">
                Trimmed to {formatPageRanges(doc.trim.keepRanges)}
            </Badge>
        )}
    </>
);

/** Where the document came from, and our copy of it. */
export const DocumentSourceLinks: FC<{ doc: ModelDocument }> = ({ doc }) => {
    const source = describeUrl(doc.sourceUrl);
    return (
        <>
            {source && doc.sourceUrl && (
                <a
                    href={doc.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
                >
                    {source.label}
                    <ExternalLink className="h-3 w-3" />
                </a>
            )}
            <a
                href={doc.storageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
            >
                Open PDF in new tab
                <ExternalLink className="h-3 w-3" />
            </a>
        </>
    );
};

/**
 * A document's family-coverage rule, stated as pills: the model-number prefixes and ranges it covers.
 * The brands and types it covers them for are the document's own (`DocumentScopePills`).
 */
export const CoveragePills: FC<{ coverage: DocumentCoverage }> = ({ coverage }) => (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Covers models:</span>
        {coverage.prefixes.map((prefix) => (
            <span
                key={prefix}
                className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-mono font-medium text-blue-800"
            >
                {prefix}
            </span>
        ))}
        {(coverage.ranges ?? []).map((range) => (
            <span
                key={formatCoverageRange(range)}
                className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-mono font-medium text-blue-800"
            >
                {formatCoverageRange(range)}
            </span>
        ))}
    </div>
);

/** How the link was made: a human published it here (Direct), or a coverage rule matched (Rule). */
export const LinkKindBadge: FC<{ direct: boolean }> = ({ direct }) => (
    <Badge
        variant="secondary"
        className="border-transparent bg-slate-100 text-slate-700"
        title={direct ? 'Published on this model by a person' : 'Linked by the document’s coverage rule'}
    >
        {direct ? 'Direct' : 'Rule'}
    </Badge>
);

const FIT_LABELS: Record<Exclude<DocumentLinkFit, 'type'>, { label: string; title: string; className: string }> = {
    family: {
        label: 'via family',
        title: 'The document lists a type in this model’s family, not its own type.',
        className: 'bg-amber-100 text-amber-900',
    },
    'other-type': {
        label: 'other type',
        title: 'The document is for other equipment types. Edit the publication to add this one, or unpublish it from the model’s page.',
        className: 'bg-rose-100 text-rose-900',
    },
    'other-brand': {
        label: 'other brand',
        title: 'The document is for other brands. Edit the publication to add this one, or unpublish it from the model’s page.',
        className: 'bg-rose-100 text-rose-900',
    },
};

/** Flags a document whose brands or types don't name this model's own; nothing when they do. */
export const LinkFitBadge: FC<{ fit: DocumentLinkFit }> = ({ fit }) => {
    if (fit === 'type') return null;
    const meta = FIT_LABELS[fit];
    return (
        <Badge variant="secondary" className={cn('border-transparent', meta.className)} title={meta.title}>
            {meta.label}
        </Badge>
    );
};

/** Human-readable reason for a declined trim proposal. */
const DECLINE_LABELS: Record<string, string> = {
    interleaved: 'languages alternate',
    'no-english': 'no English',
    'no-classifiable-text': 'no text layer',
    empty: 'no pages',
};

function declineLabel(proposal: DocumentTrimProposal): string | undefined {
    return DECLINE_LABELS[proposal.reason ?? ''] ?? proposal.reason;
}

/** What a reviewer should check before applying a proposal that carries this caution. */
function describeCaution(caution: TrimCaution, analysis: DocumentLanguageAnalysis): string {
    if (caution === 'english-in-middle') {
        return 'English sits between other languages here. Check the first and last kept pages are English, and that no English is mixed into the cut pages.';
    }
    const pages = uncertainTrimPages(analysis.pages);
    const ranges = formatPageRanges(collapseToRanges(pages));
    return pages.length === 1
        ? `The detector read page ${ranges} weakly, so it may be on the wrong side of the cut.`
        : `The detector read pages ${ranges} weakly, so they may be on the wrong side of the cut.`;
}

/**
 * Whether the server will analyze this candidate's bytes: it needs staged bytes it can read, and refuses
 * a cut record, whose pages no longer number like the original's.
 */
export function canAnalyzeLanguages(candidate: ModelDocumentCandidate | undefined): boolean {
    return !!candidate?.stagedStorageUrl && !candidate.oversize && !candidate.trim;
}

/**
 * One glanceable badge for a document's language analysis: what an English-only trim would keep, all-
 * English (nothing to do), or why the policy declined to propose one. Language badges wear purple —
 * `light` is the candidate-row variant (the source's analysis), full purple the published-document one —
 * and amber when the proposal carries cautions, which its tooltip spells out. An analysis made under an
 * older policy gets a re-analyze button beside it when the caller can run one.
 */
export const LanguageAnalysisBadge: FC<{
    analysis: DocumentLanguageAnalysis;
    light?: boolean;
    saving?: boolean;
    onReanalyze?: () => Promise<boolean>;
}> = ({ analysis, light, saving, onReanalyze }) => {
    const { proposal } = analysis;
    const cautioned = proposal.cautions.length > 0;
    const tone = cautioned
        ? 'border-transparent bg-amber-100 text-amber-900'
        : light
          ? 'border-transparent bg-purple-50 text-purple-800'
          : 'border-transparent bg-purple-100 text-purple-900';
    const badge = (
        <Badge variant="secondary" className={tone}>
            {cautioned && '⚠ '}
            {describeProposal(analysis)}
        </Badge>
    );
    return (
        <span className="inline-flex items-center gap-1">
            {cautioned ? (
                <HintTooltip
                    hint={proposal.cautions.map((caution) => (
                        <span key={caution}>{describeCaution(caution, analysis)}</span>
                    ))}
                >
                    {badge}
                </HintTooltip>
            ) : (
                badge
            )}
            {onReanalyze && isStaleAnalysis(analysis) && (
                <HintTooltip hint={describeStaleAnalysis(analysis)}>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        aria-label="Re-analyze languages"
                        disabled={saving}
                        onClick={() => void onReanalyze()}
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                </HintTooltip>
            )}
        </span>
    );
};

function describeProposal({ proposal, totalPages }: DocumentLanguageAnalysis): string {
    if (proposal.outcome === 'trim') return `English ${formatPageRanges(proposal.keepRanges)} of ${totalPages}p`;
    if (proposal.outcome === 'none') return `All English · ${totalPages}p`;
    return `No trim · ${declineLabel(proposal)}`;
}

function isStaleAnalysis(analysis: DocumentLanguageAnalysis): boolean {
    return analysis.revision < TRIM_REVISION;
}

function describeStaleAnalysis(analysis: DocumentLanguageAnalysis): string {
    return `Analyzed under language rules v${analysis.revision}; the current rules are v${TRIM_REVISION}. Re-analyze to see what they propose.`;
}

/** `128 pages: 39 en · 42 es · 42 fr · 5 unclassified` — the dialog's language rollup. */
export function describeLanguageBreakdown(analysis: DocumentLanguageAnalysis): string {
    const counts = summarizeLanguagePages(analysis);
    const parts = Object.entries(counts)
        .sort(([, a], [, b]) => b - a)
        .map(([lang, count]) => `${count} ${lang === 'unknown' ? 'unclassified' : lang}`);
    return `${analysis.totalPages} pages: ${parts.join(' · ')}`;
}

/** Apply a page cut to a candidate's staged bytes or to a published document, in place. */
export type ApplyTrim = (target: TrimTarget, docId: ModelDocumentId, cut: DocumentCut) => Promise<boolean>;

/**
 * Restore the full PDF, discarding EVERY cut made on the record — not a one-step undo. `trim.sourcePath`
 * stays pinned to the original across successive cuts, so there is no per-pass history to step back
 * through; the UI says "Restore full PDF" rather than "Undo" for exactly that reason.
 */
export type RestoreTrim = (target: TrimTarget, docId: ModelDocumentId) => Promise<boolean>;

/** The cut itself, exactly as the callable takes it — which pages, and whether the proposal chose them. */
export type DocumentCut = Pick<ApplyDocumentTrimRequest, 'selection' | 'viaProposal'>;

type TrimMode = 'language' | 'keep' | 'remove';

const TRIM_MODES: { key: TrimMode; label: string }[] = [
    { key: 'language', label: 'By language' },
    { key: 'keep', label: 'Keep pages' },
    { key: 'remove', label: 'Remove pages' },
];

/**
 * Cut pages out of a document PDF, one act reached three ways — and each mode means ONE thing.
 *
 * By language APPLIES the policy: it states what the analysis would keep and cuts exactly that. No
 * editable field, because a mode that says "I trust the analysis" while inviting you to overrule it is
 * really Keep pages wearing a costume — which is what this was before Keep and Remove existed to hold
 * that intent properly. Disagreeing is still one click: "Adjust these pages" hands the proposal's ranges
 * to Keep pages, and the cut stays a verdict ON the proposal (`viaProposal`), so an edited-then-applied
 * cut still scores as `applied-adjusted` in the accuracy corpus.
 *
 * Keep and Remove take printer syntax — "1, 4-5, 10-20, 22". They're separate modes rather than one
 * because both are natural to read off a PDF, and making someone invert "drop the Spanish half" in their
 * head is where the mistakes come from. Page numbers are only bounds-checked here when an analysis has
 * counted the pages; the server always re-validates against the real PDF, so a cut typed blind fails
 * there with the same readable message.
 */
export const TrimPdfDialog: FC<TrimPdfDialogProps> = ({ open, onOpenChange, ...form }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
            {/* Radix unmounts `DialogContent` while closed, so the form's state resets on every open for
                free — the same seam `DocumentFormDialog` below splits on. */}
            <TrimPdfForm {...form} onClose={() => onOpenChange(false)} />
        </DialogContent>
    </Dialog>
);

interface TrimPdfDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** What is being cut, for the title. */
    title: string;
    /** The language analysis of these bytes, once one has been run. */
    analysis: DocumentLanguageAnalysis | undefined;
    /** Whether analysis can be run at all — it needs staged candidate bytes to be stored against. */
    canAnalyze: boolean;
    /**
     * Pages the record has RIGHT NOW — fewer than the analysis counted once it's been cut. Ranges are
     * typed against what the reviewer can see, so this is what bounds them; the server composes them back
     * into the original's numbering. Undefined when nothing has counted the pages.
     */
    pageCount: number | undefined;
    /**
     * Whether these bytes are already a cut. It hides the language route: the stored analysis describes
     * the ORIGINAL pages, so re-offering its proposal against cut bytes would name the wrong ones.
     */
    trimmed: boolean;
    saving: boolean;
    /** What applying will do, in a sentence — a candidate and a published document differ sharply. */
    outcome: ReactNode;
    submitLabel: string;
    onAnalyze: () => Promise<boolean>;
    onApply: (cut: DocumentCut) => Promise<boolean>;
}

const TrimPdfForm: FC<Omit<TrimPdfDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void }> = ({
    title,
    analysis,
    canAnalyze,
    pageCount,
    trimmed,
    saving,
    outcome,
    submitLabel,
    onAnalyze,
    onApply,
    onClose,
}) => {
    // Only a `trim` outcome is actionable: an all-English document's `keepRanges` cover every page, and a
    // declined proposal has none — neither is a cut the server would accept. An already-cut record has no
    // language route at all (see `trimmed`), so its proposal is never offered.
    const proposedRanges = !trimmed && analysis?.proposal.outcome === 'trim' ? analysis.proposal.keepRanges : undefined;
    const modes = trimmed ? TRIM_MODES.filter(({ key }) => key !== 'language') : TRIM_MODES;
    const [mode, setMode] = useState<TrimMode>(() => initialTrimMode(analysis, canAnalyze, trimmed));
    const [rangesText, setRangesText] = useState('');
    // Whether the proposal is what put the current text in the field (via "Adjust these pages") — the
    // question `viaProposal` asks, which survives the reviewer then editing those ranges.
    const [fromProposal, setFromProposal] = useState(false);

    const selectMode = (next: TrimMode) => {
        // Keep and Remove name opposite things, so carrying text between them would silently ask for the
        // inverse of what's on screen. Language shares no field with either, so stepping through it to
        // re-read the proposal leaves a half-typed cut alone.
        if (next !== 'language' && mode !== 'language' && next !== mode) {
            setRangesText('');
            setFromProposal(false);
        }
        setMode(next);
    };

    /** Hand the proposal's ranges to Keep pages to be edited — still the proposal's call to answer for. */
    const adjustProposal = () => {
        if (!proposedRanges) return;
        setRangesText(formatPageRanges(proposedRanges));
        setFromProposal(true);
        setMode('keep');
    };

    const submit = async () => {
        // The language route applies the policy verbatim — there is no field to read it back out of.
        if (mode === 'language') {
            if (!proposedRanges) return;
            const cut: DocumentCut = { selection: { mode: 'keep', ranges: proposedRanges }, viaProposal: true };
            if (await onApply(cut)) onClose();
            return;
        }
        let ranges: PageRange[];
        try {
            ranges = parsePageRanges(rangesText, pageCount);
        } catch (error) {
            toast.error('Fix the page ranges', {
                description: error instanceof Error ? error.message : String(error),
            });
            return;
        }
        if (await onApply({ selection: { mode, ranges }, viaProposal: fromProposal })) onClose();
    };

    const canSubmit = mode === 'language' ? !!proposedRanges : !!rangesText.trim();

    return (
        <>
            <DialogHeader>
                <DialogTitle>Trim “{title}”</DialogTitle>
                <DialogDescription>{outcome}</DialogDescription>
            </DialogHeader>
            <div className="flex gap-1 rounded-md bg-muted p-1">
                {modes.map(({ key, label }) => (
                    <Button
                        key={key}
                        type="button"
                        size="sm"
                        variant={mode === key ? 'default' : 'ghost'}
                        className="flex-1"
                        onClick={() => selectMode(key)}
                    >
                        {label}
                    </Button>
                ))}
            </div>
            {mode === 'language' ? (
                <LanguageModeNotes
                    analysis={analysis}
                    canAnalyze={canAnalyze}
                    saving={saving}
                    onAnalyze={onAnalyze}
                    onAdjust={adjustProposal}
                />
            ) : (
                <div className="space-y-2">
                    <Label htmlFor="trim-ranges">{mode === 'remove' ? 'Pages to remove' : 'Pages to keep'}</Label>
                    <Input
                        id="trim-ranges"
                        value={rangesText}
                        onChange={(event) => setRangesText(event.target.value)}
                        placeholder="1, 4-5, 10-20, 22"
                        disabled={saving}
                        autoComplete="off"
                    />
                    <div className="field-hint">
                        Pages and ranges, like a printer{pageCount ? ` · ${pageCount} pages total` : ''}
                        {trimmed ? ' (numbered as this PDF reads now)' : ''}
                    </div>
                </div>
            )}
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button type="button" disabled={saving || !canSubmit} onClick={() => void submit()}>
                    {submitLabel}
                </Button>
            </DialogFooter>
        </>
    );
};

/**
 * Where the dialog opens: the language route whenever it can still act — it has a cut to offer, or one
 * worth running — and by hand when it can't (already cut, already all English, declined, or nothing to
 * analyze).
 */
function initialTrimMode(
    analysis: DocumentLanguageAnalysis | undefined,
    canAnalyze: boolean,
    trimmed: boolean,
): TrimMode {
    if (trimmed) return 'keep';
    if (analysis) return analysis.proposal.outcome === 'trim' ? 'language' : 'keep';
    return canAnalyze ? 'language' : 'keep';
}

/**
 * The language route, stated rather than editable: run the analysis, see exactly what applying it keeps,
 * or hear why it has nothing to offer. "Adjust these pages" is the one way out — into Keep pages, holding
 * the proposal's ranges — so overruling the policy is a deliberate step rather than a stray keystroke.
 */
const LanguageModeNotes: FC<{
    analysis: DocumentLanguageAnalysis | undefined;
    canAnalyze: boolean;
    saving: boolean;
    onAnalyze: () => Promise<boolean>;
    onAdjust: () => void;
}> = ({ analysis, canAnalyze, saving, onAnalyze, onAdjust }) => {
    if (!analysis) {
        return canAnalyze ? (
            <div className="space-y-2 rounded-md border p-3">
                <div className="section-description">
                    Read every page’s language and propose an English-only cut. Takes a few seconds.
                </div>
                <Button type="button" size="sm" disabled={saving} onClick={() => void onAnalyze()}>
                    Analyze languages
                </Button>
            </div>
        ) : (
            <div className="rounded-md border p-3 section-description">
                Language analysis is stored on a candidate’s staged bytes, and this document has none. Pick the pages by
                hand instead.
            </div>
        );
    }
    const { proposal, totalPages } = analysis;
    return (
        <div className="space-y-1 rounded-md border p-3 text-sm">
            <div>{describeLanguageBreakdown(analysis)}</div>
            {canAnalyze && isStaleAnalysis(analysis) && (
                <div className="flex items-baseline justify-between gap-3 text-xs text-amber-800">
                    <span>{describeStaleAnalysis(analysis)}</span>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto shrink-0 p-0"
                        disabled={saving}
                        onClick={() => void onAnalyze()}
                    >
                        Re-analyze
                    </Button>
                </div>
            )}
            {proposal.outcome === 'trim' && (
                <>
                    <div className="font-medium">
                        Keeps pages {formatPageRanges(proposal.keepRanges)} — {proposal.keptPages} of {totalPages}
                    </div>
                    <TrimCautions analysis={analysis} />
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="field-hint">Detector confidence {Math.round(proposal.confidence * 100)}%</span>
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0"
                            disabled={saving}
                            onClick={onAdjust}
                        >
                            Adjust these pages
                        </Button>
                    </div>
                </>
            )}
            {proposal.outcome === 'none' && (
                <>
                    <TrimCautions analysis={analysis} />
                    <div className="field-hint">
                        Already all English — nothing to cut by language. Pick pages by hand if you want a different
                        cut.
                    </div>
                </>
            )}
            {proposal.outcome === 'declined' && (
                <div className="field-hint">No cut proposed ({declineLabel(proposal)}) — pick pages by hand.</div>
            )}
        </div>
    );
};

const TrimCautions: FC<{ analysis: DocumentLanguageAnalysis }> = ({ analysis }) => {
    const { cautions } = analysis.proposal;
    if (cautions.length === 0) return null;
    return (
        <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2 text-xs text-amber-800">
            {cautions.map((caution) => (
                <li key={caution}>⚠ {describeCaution(caution, analysis)}</li>
            ))}
        </ul>
    );
};

// Inline, pageable PDF preview via the browser's native viewer (no extra deps). If a host refuses to be
// framed (X-Frame-Options) the frame renders blank — callers pair it with an "Open in new tab" link.
// `referrerPolicy=no-referrer` avoids leaking the admin URL. We deliberately do NOT sandbox: Chrome's
// built-in PDF viewer blanks under a `sandbox` without `allow-scripts`; these are our own Storage URLs.
export const PdfPreview: FC<{ url: string; title: string; className?: string }> = ({ url, title, className }) => (
    <div className="overflow-hidden rounded-md border bg-muted/40">
        <iframe src={url} title={title} referrerPolicy="no-referrer" className={className ?? 'h-[60vh] w-full'} />
    </div>
);

/** Tag-enum order, split into the picker's three groups: app-visible kinds, library kinds, metadata. */
const TAG_GROUPS: { heading: string; tags: ModelDocTag[] }[] = [
    { heading: 'Shown in the app', tags: ModelDocTagSchema.options.filter((tag) => APP_VISIBLE_TAGS.includes(tag)) },
    {
        heading: 'Library only',
        tags: ModelDocTagSchema.options.filter(
            (tag) => !APP_VISIBLE_TAGS.includes(tag) && !METADATA_TAGS.includes(tag),
        ),
    },
    { heading: 'Metadata', tags: ModelDocTagSchema.options.filter((tag) => METADATA_TAGS.includes(tag)) },
];

const CLASSIFICATION_HINT =
    'Your classification wins — the verifier’s verdict never blocks it. Any app tag serves the document in the app; a purely library-only set records it against the model without exposing it anywhere.';

/** The coverage field's wiring, when the form offers one. Its preview reads the scope rows above it. */
export interface DocumentFormCoverage {
    value: CoverageDraft;
    onChange: (next: CoverageDraft) => void;
    /** Predicates the verifier claims the document covers, offered as one-click chips. */
    suggestions?: CoverageSuggestion[];
    /** The model this publish links, and its opt-out. Omit on forms that link nothing (tag/rule edits). */
    publishTarget?: CoveragePublishTarget;
}

export interface DocumentFormProps {
    /** Static label, or a function of the currently-checked tags (e.g. "Publish" vs "Confirm"). */
    submitLabel: string | ((checked: ModelDocTag[]) => string);
    initialTags: ModelDocTag[];
    initialTitle?: string;
    saving: boolean;
    /**
     * `notify` is the "Notifications" toggle: true (the default) publishes normally, false publishes
     * silently. Always passed; on forms without the toggle (`notifiable` unset) it is always true.
     * Re-tagging an existing document ignores it — a same-bytes edit never announces regardless.
     */
    onSubmit: (tags: ModelDocTag[], title: string | undefined, notify: boolean) => Promise<boolean>;
    /** Close/dismiss without submitting; also called after a successful submit. */
    onCancel: () => void;
    /**
     * Opt this form into the notifications toggle — for fresh publishes that CAN announce a new manual.
     * It still only renders while the `owner-manual` tag is checked (the only kind that notifies); on any
     * other selection the publish stays loud-but-inert.
     */
    notifiable?: boolean;
    /** Green submit button — for actions that actually publish (vs. plain edits). */
    submitVariant?: 'default' | 'success';
    /** Renders the Brands and Types rows. Coverage needs them: its preview reads them. */
    scope?: DocumentScopeFieldsProps;
    /** Renders the Coverage row. Omit where coverage doesn't apply. */
    coverage?: DocumentFormCoverage;
    /** What saving would change beyond this form, shown above the buttons. */
    notice?: ReactNode;
    /**
     * Why this form cannot be submitted, static or a function of the checked tags; shown in place of the
     * notice, with Submit disabled.
     */
    blockedReason?: string | ((checked: ModelDocTag[]) => string | undefined);
    /** Optional extra controls rendered above the rows. */
    children?: ReactNode;
}

/**
 * The one document form — behind "Confirm as…" (candidates), "Edit publication" (confirmed docs), and
 * Offer file. Label/value rows: Classification, Brands, Types, Coverage, Title, Notifications. Tags submit as the
 * CHECKED set verbatim (replace semantics), shown as pills with the full option list tucked into a
 * grouped dropdown — the reviewer's screen is for the PDF, not for eleven checkboxes they aren't picking.
 *
 * State initializes on mount — callers unmount the form when it closes (Radix dialogs do this for free;
 * inline placements render it conditionally), so a fresh open never inherits another row's edits.
 */
export const DocumentForm: FC<DocumentFormProps> = ({
    submitLabel,
    initialTags,
    initialTitle,
    saving,
    onSubmit,
    onCancel,
    notifiable,
    submitVariant = 'default',
    scope,
    coverage,
    notice,
    blockedReason,
    children,
}) => {
    const [checked, setChecked] = useState<Set<ModelDocTag>>(new Set(initialTags));
    const [docTitle, setDocTitle] = useState(initialTitle ?? '');
    const [notify, setNotify] = useState(true);
    // A coverage predicate typed but never added. Submitting would drop it silently — the value is only
    // real once it's a chip — so Save refuses and hands the reviewer back to the field to resolve it.
    const [pendingPredicate, setPendingPredicate] = useState<string | undefined>(undefined);
    const [pendingBlocked, setPendingBlocked] = useState(false);

    // Stable: the Coverage field reports through this from an effect.
    const onPendingChange = useCallback((pending: string | undefined) => {
        setPendingPredicate(pending);
        if (!pending) setPendingBlocked(false);
    }, []);

    const toggle = (tag: ModelDocTag, value: boolean) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (value) next.add(tag);
            else next.delete(tag);
            return next;
        });
    };

    const checkedTags = ModelDocTagSchema.options.filter((tag) => checked.has(tag));
    const blocked = typeof blockedReason === 'function' ? blockedReason(checkedTags) : blockedReason;

    // The notification only ever fires for an owner manual (the trigger keys off `ownerManualDocument`), so
    // the toggle only means anything when that tag is checked — hidden otherwise, and the opt-out never
    // stamps `publishedSilently` on a non-manual (inert, but tidier not to).
    //
    // A publish that links no model is inert the same way, and less obviously: the document then reaches
    // its models through coverage backfill, which links SILENTLY by design. Leaving the toggle on screen
    // would promise an announcement nobody receives.
    const linksAModel = !coverage?.publishTarget || publishLinksTarget(coverage.publishTarget);
    const canNotify = notifiable && checked.has('owner-manual') && linksAModel;

    const submit = async () => {
        // First, ahead of the tag check: it's what the reviewer just typed, and the prompt renders at the
        // field rather than in a corner toast.
        if (pendingPredicate) {
            setPendingBlocked(true);
            return;
        }
        if (checkedTags.length === 0) {
            toast.error('Pick at least one tag');
            return;
        }
        if (await onSubmit(checkedTags, docTitle.trim() || undefined, canNotify ? notify : true)) onCancel();
    };

    return (
        <div className="space-y-3">
            {children}
            <FormRow label="Classification" hint={CLASSIFICATION_HINT}>
                <div className="flex flex-wrap items-center gap-1.5">
                    {checkedTags.map((tag) => (
                        <Badge
                            key={tag}
                            variant="secondary"
                            className="gap-1 border-transparent bg-slate-100 text-slate-900"
                        >
                            {DOC_TAG_LABELS[tag]}
                            <button
                                type="button"
                                aria-label={`Remove ${DOC_TAG_LABELS[tag]}`}
                                onClick={() => toggle(tag, false)}
                                disabled={saving}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button type="button" variant="outline" size="sm" className="h-7" disabled={saving}>
                                Add tag
                                <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                            {TAG_GROUPS.map((group, index) => (
                                <div key={group.heading}>
                                    {index > 0 && <DropdownMenuSeparator />}
                                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                                        {group.heading}
                                    </DropdownMenuLabel>
                                    {group.tags.map((tag) => (
                                        <DropdownMenuCheckboxItem
                                            key={tag}
                                            checked={checked.has(tag)}
                                            // Stay open: picking tags is usually picking more than one.
                                            onSelect={(event) => event.preventDefault()}
                                            onCheckedChange={(value) => toggle(tag, value === true)}
                                        >
                                            {DOC_TAG_LABELS[tag]}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </div>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </FormRow>
            {scope && <DocumentScopeFields {...scope} />}
            {coverage && scope && (
                <FormRow label="Coverage" hint={COVERAGE_HINT}>
                    <CoverageFields
                        value={coverage.value}
                        onChange={coverage.onChange}
                        scope={scope.value}
                        saving={saving}
                        suggestions={coverage.suggestions}
                        publishTarget={coverage.publishTarget}
                        onPendingChange={onPendingChange}
                        pendingBlocked={pendingBlocked}
                    />
                </FormRow>
            )}
            <FormRow label="Title">
                <Input
                    id="doc-title"
                    placeholder="Defaults to the first tag’s label"
                    value={docTitle}
                    onChange={(event) => setDocTitle(event.target.value)}
                    disabled={saving}
                    autoComplete="off"
                    className="h-8 max-w-md"
                />
            </FormRow>
            {canNotify && (
                <FormRow label="Notifications">
                    <div className="flex items-center gap-2 pt-1.5">
                        <Checkbox
                            id="doc-notify"
                            checked={notify}
                            onCheckedChange={(value) => setNotify(value === true)}
                            disabled={saving}
                        />
                        <Label htmlFor="doc-notify" className="cursor-pointer text-sm font-normal">
                            Tell affected owners
                        </Label>
                    </div>
                </FormRow>
            )}
            {blocked ? (
                <div
                    role="alert"
                    className="rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2 text-xs text-amber-800"
                >
                    {blocked}
                </div>
            ) : (
                notice
            )}
            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:items-center sm:justify-end sm:space-x-2">
                <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={submitVariant}
                    onClick={() => void submit()}
                    disabled={saving || !!blocked}
                >
                    {typeof submitLabel === 'function' ? submitLabel(checkedTags) : submitLabel}
                </Button>
            </div>
        </div>
    );
};

/**
 * `DocumentForm` in a modal — the presentation "Edit publication" and candidate review keep. (Candidate review
 * renders the form INLINE above the PDF preview instead, so the reviewer can classify while reading.)
 * Radix unmounts `DialogContent` while closed, so the form's state resets on every open for free.
 */
export const DocumentFormDialog: FC<
    Omit<DocumentFormProps, 'onCancel'> & {
        open: boolean;
        onOpenChange: (open: boolean) => void;
        title: string;
    }
> = ({ open, onOpenChange, title, ...form }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            <DocumentForm {...form} onCancel={() => onOpenChange(false)} />
        </DialogContent>
    </Dialog>
);
